import { colors } from '@/styles';

/** Keys returned by `itemStatusTag`, one per kind of tag the picker shows. */
export const ITEM_TAG_KEYS = {
    RECEIVED: 'received',
    SHORTAGE: 'shortage',
    EXCEPTION: 'exception',
    NOT_RECEIVED: 'notReceived',
};

/** Tag text for the fixed kinds. An open exception uses its own type label instead. */
export const ITEM_TAG_LABELS = {
    [ITEM_TAG_KEYS.RECEIVED]: 'Received',
    [ITEM_TAG_KEYS.SHORTAGE]: 'Shortage',
    [ITEM_TAG_KEYS.NOT_RECEIVED]: 'Not received',
};

/** Text colour for each tag key, used by the item picker. Tags are coloured text, not filled pills. */
export const ITEM_TAG_COLORS = {
    [ITEM_TAG_KEYS.RECEIVED]: colors.greens.primary,
    [ITEM_TAG_KEYS.SHORTAGE]: colors.oranges.primary,
    [ITEM_TAG_KEYS.EXCEPTION]: colors.oranges.primary,
    [ITEM_TAG_KEYS.NOT_RECEIVED]: colors.greys.primary,
};

/** Shown when a split call fails without a server message (network error). */
export const GENERIC_SPLIT_ERROR = "Couldn't split the order. Try again.";

/** Shown when the modal's own read of the order fails. */
export const READ_ERROR = "Couldn't load the order. Close this and try again.";

/** Shown when the order has a blocker code that has no reason line of its own (for example `ALREADY_SPLIT`). */
export const FALLBACK_BLOCKED = "This order can't be split right now.";

/** Shown when the re-read after a failed split also fails, so the list may be out of date. */
export const REFRESH_ERROR = "We couldn't refresh the order. Close this and try again.";

/** Helper tooltip for the Billable switch. */
export const BILLABLE_HELP = 'Off means the shipper is charged $0 for this order. The carrier is still paid.';
/** Shown in an empty new-order column once no items are left in the tray. */
export const COLUMN_EMPTY_ERROR = 'Add at least one item';

/** Shown in a new-order column whose item lines are all intangible. */
export const COLUMN_NEEDS_PHYSICAL_ERROR = 'Needs at least one physical item';

/** Text of the split board's submit button. */
export const SPLIT_ORDER_LABEL = 'Split order';

/** Text of the split board's submit button while the split runs. */
export const SPLITTING_LABEL = 'Splitting…';

/** Shown under a new-order column's header while its Billable switch is off. */
export const NOT_BILLABLE_NOTE = 'Not billable — the shipper is charged $0 for this order';
/** Helper tooltip when the child is invoiced and billing can't change. */
export const BILLABLE_INVOICED_HELP = "This order has been invoiced, so billing can't be changed.";
/** Shown when the billable change returns no order. */
export const BILLABLE_ERROR = "Couldn't change billing. Try again.";

/** The kinds of card the split board shows. Items mode places item lines, services mode the parent's services, and claims mode its open claims. */
export const BOARD_MODES = {
    /** Item lines (and portions of a divided line). */
    ITEMS: 'items',
    /** The parent's service rows. Each goes whole to one order. */
    SERVICES: 'services',
    /** The parent's open claims (status not Rejected or Closed). Each goes whole to one order. */
    CLAIMS: 'claims',
};

/** Heading of the tray that holds every card not yet placed on a new order. */
export const UNASSIGNED_TITLE = 'Unassigned';

/** Drop hint shown in a new-order column that has no item lines yet. */
export const EMPTY_COLUMN_HINT = 'Drag items here';

/** Label shown on an item card for a line that isn't a physical item. */
export const INTANGIBLE_LABEL = 'Intangible';

/** Drop target id for the Unassigned tray. Order columns use their layout key as the id. */
export const TRAY_DROPPABLE_ID = 'unassigned';

/** Drag type for item cards, so other kinds of card can't be dropped into an item list. */
export const ITEM_DRAG_TYPE = 'ITEM';

/** Drag type for service cards, so a service can't be dropped into an item list or the reverse. */
export const SERVICE_DRAG_TYPE = 'SERVICE';

/** Drag type for claim cards, so a claim can't be dropped into an item or service list or the reverse. */
export const CLAIM_DRAG_TYPE = 'CLAIM';

/** Text of each option on the board's mode switch. */
export const BOARD_MODE_LABELS = {
    /** Label of the items option. */
    [BOARD_MODES.ITEMS]: 'Items',
    /** Label of the services option. */
    [BOARD_MODES.SERVICES]: 'Services',
    /** Label of the claims option. */
    [BOARD_MODES.CLAIMS]: 'Claims',
};

/** Intro above the board in items mode. */
export const ITEMS_INTRO =
    'Drag each item onto the new order it belongs to. Each new order is scheduled and delivered independently.';

/** Intro above the board in services mode. */
export const SERVICES_INTRO =
    'Drag each service onto the new order it belongs to. It moves with its quantity and settings.';

/** Intro above the board in claims mode. */
export const CLAIMS_INTRO =
    'Drag each open claim onto the new order it belongs to. Every open claim has to go to one of the new orders.';

/** Drop hint shown in services mode in a new-order column that has no services yet. */
export const EMPTY_SERVICES_HINT = 'Drop services here';

/** Drop hint shown in claims mode in a new-order column that has no claims yet. */
export const EMPTY_CLAIMS_HINT = 'Drop claims here';

/** Collapsed item summary in services mode for a column with no item lines yet. */
export const NO_ITEMS_SUMMARY = 'No items yet';

/** Note on a service card whose service has to be confirmed (`require_confirm`). */
export const NEEDS_CONFIRMATION_LABEL = 'Needs confirmation';

/** Text of the menu button on each card that moves it without dragging. */
export const MOVE_TO_LABEL = 'Move to';

/** Accessible name prefix of the info button on each card that opens its details dialog. */
export const DETAILS_LABEL = 'Details';

/** Delay before a card's full-name tooltip opens, so a quick pass or a drag doesn't flash it. */
export const NAME_TOOLTIP_DELAY_MS = 500;

/** Text of the button that adds another new-order column. */
export const ADD_ORDER_LABEL = '+ Add order';

/** Tooltip on a column's remove control. */
export const REMOVE_ORDER_HELP = 'Remove this order. Its items, services, and claims go back to Unassigned.';

/** Title of the prompt shown when a card that can be divided is moved. */
export const DIVIDE_TITLE = 'How many to move?';

/** Screen reader name of the quantity input in the divide prompt. */
export const DIVIDE_QUANTITY_LABEL = 'Quantity to move';

/** Confirm button of the divide prompt. */
export const DIVIDE_MOVE_LABEL = 'Move';

/** Cancel button of the divide prompt. */
export const DIVIDE_CANCEL_LABEL = 'Cancel';

/** Screen reader name of the divide prompt's − button. */
export const DIVIDE_FEWER_LABEL = 'Move one fewer';

/** Screen reader name of the divide prompt's + button. */
export const DIVIDE_MORE_LABEL = 'Move one more';
